import React, { useMemo, useState } from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import type { JsonObject } from "@/types/json"
import { cn } from "@/utils/cn"

import { useTestSetEditorContext } from "../../../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../../../event-bus"
import type { EditableTestCase } from "../../../model"
import {
  formatValueForDisplay,
  parseValueFromString,
  type MetadataColumnType,
} from "../../../utils/metadataColumnType"
import { getPendingEditForTestCase } from "../../../utils/pendingChangesValidation"
import { DraggableListEditor } from "./DraggableListEditor"
import { InputCellWrapper } from "./InputCellWrapper"

interface MetadataColumnCellProps {
  testCase: EditableTestCase
  columnKey: string
  columnType: MetadataColumnType
  width: string
  isExpanded: boolean
  isMarkedForDeletion?: boolean
}

function getValueAt(
  meta: JsonObject | null | undefined,
  key: string,
): { present: boolean; value: unknown } {
  if (!meta || typeof meta !== "object") {
    return { present: false, value: undefined }
  }
  if (!(key in meta)) {
    return { present: false, value: undefined }
  }
  return { present: true, value: meta[key] }
}

function writeValueInto(
  meta: JsonObject | null | undefined,
  key: string,
  value: unknown,
): JsonObject {
  const next: JsonObject = { ...(meta ?? {}) }
  if (value === null) {
    delete next[key]
  } else {
    next[key] = value as JsonObject[string]
  }
  return next
}

function parseListItems(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item))
  }
  return []
}

// ─────────────────────────────────────────────────────────────────────────────
// MetadataColumnCell
// ─────────────────────────────────────────────────────────────────────────────

export const MetadataColumnCell: React.FC<MetadataColumnCellProps> = ({
  testCase,
  columnKey,
  columnType,
  width,
  isExpanded,
  isMarkedForDeletion = false,
}) => {
  const dispatch = useEventDispatcher()
  const { pendingChanges } = useTestSetEditorContext()
  const pendingEdit = getPendingEditForTestCase(testCase.id, pendingChanges)

  const liveMeta: JsonObject | null =
    pendingEdit?.expected_output_json ?? testCase.expected_output_json ?? null

  const { present, value } = useMemo(
    () => getValueAt(liveMeta, columnKey),
    [liveMeta, columnKey],
  )

  const displayValue = useMemo(
    () => (present ? formatValueForDisplay(value, columnType) : ""),
    [present, value, columnType],
  )

  const commit = (nextValue: unknown) => {
    const nextMeta = writeValueInto(
      testCase.expected_output_json ?? null,
      columnKey,
      nextValue,
    )
    dispatch(EVENTS.TEST_CASE_UPDATE, {
      testCaseId: testCase.id,
      updates: { expected_output_json: nextMeta },
    })
  }

  if (columnType === "list") {
    return (
      <InputCellWrapper
        width={width}
        isExpanded={isExpanded}
        isDisabled={isMarkedForDeletion}
        ariaLabel={`Edit ${columnKey}`}
      >
        {({ isEditing, isExpanded: cellIsExpanded }) => (
          <ListCell
            key={`${testCase.id}:${columnKey}`}
            value={present ? value : undefined}
            isEditing={isEditing}
            isExpanded={cellIsExpanded}
            isMarkedForDeletion={isMarkedForDeletion}
            onCommit={commit}
          />
        )}
      </InputCellWrapper>
    )
  }

  return (
    <InputCellWrapper
      width={width}
      isExpanded={isExpanded}
      isDisabled={isMarkedForDeletion}
      ariaLabel={`Edit ${columnKey}`}
    >
      {({ isEditing }) => {
        if (isEditing) {
          return (
            <MetadataColumnEditor
              key={`${testCase.id}:${columnKey}`}
              initial={displayValue}
              columnType={columnType}
              onCommit={commit}
            />
          )
        }

        if (!present) {
          return (
            <div className="text-sm text-coval-text-muted select-none leading-[20px]">
              —
            </div>
          )
        }

        return (
          <div
            className={cn(
              "text-sm text-coval-text whitespace-pre-wrap break-words",
              columnType === "dict" ? "font-mono text-xs" : undefined,
              isMarkedForDeletion ? "opacity-50 line-through" : undefined,
            )}
          >
            {displayValue}
          </div>
        )
      }}
    </InputCellWrapper>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ListCell — read mode is bespoke; edit mode reuses DraggableListEditor
// ─────────────────────────────────────────────────────────────────────────────

interface ListCellProps {
  value: unknown
  isEditing: boolean
  isExpanded: boolean
  isMarkedForDeletion: boolean
  onCommit: (value: unknown) => void
}

const ListCell: React.FC<ListCellProps> = ({
  value,
  isEditing,
  isExpanded,
  isMarkedForDeletion,
  onCommit,
}) => {
  const items = parseListItems(value)

  const commitItems = (next: string[]) => {
    const filtered = next.filter((i) => i.trim() !== "")
    onCommit(filtered.length === 0 ? null : filtered)
  }

  // ── Edit mode ─────────────────────────────────────────────────────────────
  if (isEditing) {
    return (
      <DraggableListEditor
        items={items}
        onItemsChange={commitItems}
        addLabel="item"
        deleteLabel="item"
        renderItem={({ value, index, onChange, onKeyDown, registerRef }) => (
          <textarea
            ref={(el) => {
              registerRef(el)
              if (el) {
                el.style.height = "0px"
                el.style.height = `${el.scrollHeight}px`
              }
            }}
            value={value}
            rows={1}
            onChange={(e) => {
              onChange(e.target.value)
              e.target.style.height = "0px"
              e.target.style.height = `${e.target.scrollHeight}px`
            }}
            onKeyDown={onKeyDown}
            placeholder="Add item..."
            className="w-full bg-transparent border-none p-0 text-sm text-coval-text outline-none resize-none min-w-0 leading-snug"
            aria-label={`List item ${index + 1}`}
          />
        )}
      />
    )
  }

  // ── Read mode ─────────────────────────────────────────────────────────────
  if (items.length === 0) {
    return (
      <div className="text-sm text-coval-text-muted select-none leading-[20px]">
        —
      </div>
    )
  }

  const MAX_COLLAPSED = 3
  const displayItems = isExpanded ? items : items.slice(0, MAX_COLLAPSED)
  const remaining = isExpanded ? 0 : Math.max(0, items.length - MAX_COLLAPSED)

  return (
    <>
      <ul className="list-disc list-outside pl-5 space-y-3 w-full min-w-0">
        {displayItems.map((item, index) => (
          <li
            key={`item-${index}`}
            className={cn(
              "text-sm leading-tight whitespace-pre-wrap break-words text-coval-text",
              isMarkedForDeletion ? "line-through opacity-50" : undefined,
            )}
          >
            {item}
          </li>
        ))}
      </ul>
      {remaining > 0 && (
        <div className="text-tiny text-coval-text-muted mt-3">
          +{remaining} more
        </div>
      )}
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// MetadataColumnEditor  (non-list types)
// ─────────────────────────────────────────────────────────────────────────────

interface EditorProps {
  initial: string
  columnType: MetadataColumnType
  onCommit: (value: unknown) => void
}

const MetadataColumnEditor: React.FC<EditorProps> = ({
  initial,
  columnType,
  onCommit,
}) => {
  const [raw, setRaw] = useState(initial)
  const [error, setError] = useState<string | null>(null)

  const handleCommit = () => {
    const parsed = parseValueFromString(raw, columnType)
    if (!parsed.ok) {
      setError(parsed.error)
      return
    }
    setError(null)
    onCommit(parsed.value)
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleCommit()
    }
  }

  const commonInputClassName =
    "bg-transparent shadow-none border-none p-0 h-auto min-h-0 text-sm text-coval-text focus-visible:ring-0 focus:border-transparent"

  if (columnType === "bool") {
    const lower = raw.toLowerCase()
    const selectValue =
      lower === "true" ? "true" : lower === "false" ? "false" : "empty"
    return (
      <div className="flex flex-col gap-1">
        <Select
          value={selectValue}
          onValueChange={(key) => {
            if (key === "empty") {
              setRaw("")
              onCommit(null)
            } else {
              setRaw(key)
              onCommit(key === "true")
            }
          }}
        >
          <SelectTrigger aria-label="Boolean value" autoFocus>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">true</SelectItem>
            <SelectItem value="false">false</SelectItem>
            <SelectItem value="empty">(empty)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )
  }

  if (columnType === "dict") {
    return (
      <div className="flex flex-col gap-1">
        <JsonCodeEditor
          value={raw}
          onChange={setRaw}
          onBlur={handleCommit}
          rows={3}
          chromeless
          placeholder={'{\n  "key": "value"\n}'}
          ariaLabel="dict value"
          error={error}
        />
      </div>
    )
  }

  if (columnType === "int" || columnType === "float") {
    return (
      <div className="flex flex-col gap-1">
        <Input
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onBlur={handleCommit}
          onKeyDown={handleKeyDown}
          type="number"
          autoFocus
          className={commonInputClassName}
          aria-label={`${columnType} value`}
        />
        {error && <div className="text-xs text-coval-danger-text">{error}</div>}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <textarea
        ref={(el) => {
          if (el) {
            el.style.height = "0px"
            el.style.height = `${el.scrollHeight}px`
            el.focus()
          }
        }}
        value={raw}
        onChange={(e) => {
          setRaw(e.target.value)
          e.target.style.height = "0px"
          e.target.style.height = `${e.target.scrollHeight}px`
        }}
        onBlur={handleCommit}
        onKeyDown={handleKeyDown}
        className="w-full bg-transparent border-none p-0 text-sm text-coval-text resize-none outline-none"
        aria-label={`${columnType} value`}
      />
      {error && <div className="text-xs text-coval-danger-text">{error}</div>}
    </div>
  )
}
